import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  useUpdateTechnicianProfileContact,
  type TechnicianProfile,
} from '@/routes/technicians/useTechnicianProfile';

/**
 * The ONE thing a technician may edit on their own profile (M1-24) —
 * `PUT /technician-profiles/{id}/contact` accepts nothing else, so this
 * dialog asks for nothing else either. Everyone else's profile is edited
 * through {@link import('./TechnicianProfileFormDialog').TechnicianProfileFormDialog}
 * instead.
 */
export function EmergencyContactDialog({
  profile,
  onClose,
  onSaved,
}: {
  profile: TechnicianProfile;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const updateContact = useUpdateTechnicianProfileContact(profile.id);

  const [name, setName] = useState(profile.emergencyContactName ?? '');
  const [phone, setPhone] = useState(profile.emergencyContactPhone ?? '');

  function submit() {
    updateContact.mutate(
      { emergencyContactName: name.trim() || null, emergencyContactPhone: phone.trim() || null },
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
      },
    );
  }

  return (
    <Dialog
      titleId="emergency-contact-title"
      title={t('technicianProfile.contact.title')}
      onClose={onClose}
      testId="emergency-contact-dialog"
      dismissLabel={t('technicianProfile.contact.dismiss')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" size="sm" disabled={updateContact.isPending} onClick={submit}>
            {updateContact.isPending
              ? t('technicianProfile.contact.saving')
              : t('technicianProfile.contact.confirm')}
          </Button>
        </>
      }
    >
      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('technicianProfile.contact.name')}</span>
        <input
          className={FIELD_CLASS}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('technicianProfile.contact.phone')}</span>
        <input
          className={FIELD_CLASS}
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
        />
      </label>

      {updateContact.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('technicianProfile.contact.failed')}
        </p>
      ) : null}
    </Dialog>
  );
}
